import { interfaces } from "inversify";
import {
    AccountConfig,
    AccountConfigData,
    accountConfigRTTI,
    configDataContainerRTTI,
    AccountConfigOptions
} from "~/modules/config";
import { ConfigDataContainer } from "~/modules/config/AccountConfig/ConfigDataContainer";
import { LoggerFactory, loggerFactoryRTTI, LoggerName } from "~/modules/logger";
import { ThrowErrorFunction, throwErrorRTTI, ErrorSeverity, ErrorCode } from "~/modules/error";
import { LocalStorage, localStorageRTTI, LocalStorageKeys } from "~/modules/storage";
import { ClientOptions, clientOptionsRTTI } from "~/modules/client";
import { ConfigurationService } from "~/backend/generated/Configuration/api/configuration.serviceInterface";
import { configurationServiceRTTI } from "~/backend/backend.rtti";
import { extractFileNameFromPath, extractModuleFromPath } from "~/utils/extractFromPath";

const metadata = {
    module: extractModuleFromPath(__dirname),
    eventSource: extractFileNameFromPath(__filename)
};




export type AccountConfigProvider = (sessionToken: string, options?: AccountConfigOptions) => Promise<AccountConfig>;




export const accountConfigProvider =
    (container: interfaces.Container) =>
    async (sessionToken: string, options: AccountConfigOptions = {}): Promise<AccountConfig> => {
        const dataContainer = container.get<ConfigDataContainer<AccountConfigData>>(configDataContainerRTTI);
        const configurationService: ConfigurationService =
            container.get<ConfigurationService>(configurationServiceRTTI);
        const throwError = container.get<ThrowErrorFunction>(throwErrorRTTI);
        const storage = container.get<LocalStorage>(localStorageRTTI);
        const logger = container.get<LoggerFactory>(loggerFactoryRTTI)(LoggerName.Config);
        const clientOptions: ClientOptions = container.get<ClientOptions>(clientOptionsRTTI);

        let configData: AccountConfigData;

        const uiVersion = clientOptions?.accountConfig.query?.UiVersion || options?.query?.UiVersion;

        try {
            configData = await configurationService.fetchConfiguration("UiVersion", uiVersion, {
                token: sessionToken
            });
            storage.setCachedItem(LocalStorageKeys.AccountConfig, configData);
        } catch (err) {
            const cachedConfig = storage.getCachedItem<AccountConfigData>(LocalStorageKeys.AccountConfig);

            if (cachedConfig === undefined) {
                const message = `Failed to fetch account configuration: ${err}. No cache found.`;
                const errorCode = err.code || ErrorCode.Unknown;
                return throwError(errorCode, { ...metadata, severity: ErrorSeverity.Error }, message);
            }

            logger.warn(`Failed to fetch account configuration: ${err}. Using cache instead`);
            configData = cachedConfig;
        }
        dataContainer.set(configData);

        const accountConfig = container.get<AccountConfig>(accountConfigRTTI);
        return accountConfig;
    };
